import React, { useState, useMemo, useCallback, useEffect } from 'react'
import {
  Button, Stack, Typography, FormControl, Select, MenuItem
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useIsMobile } from '@/hooks/Responsive'
import BoardList from '@components/Board/BoardList'
import BoardListTable from '@components/Board/BoardListTable'
import { fetchMyInquiryList, fetchMyCourseComboList } from '@/api/mypage'
import { useAuth } from '@/components/AuthContext'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'
import { visuallyHidden } from '@mui/utils'

export function createInquiryTableColumns({ isMobile, lang }) {
  const renderTitle = (v, row) => (
    <Typography
      component="a"
      href={buildLangHref(`/mypage/inquiry/${row.bulletinId}?type=${row.bbsCategory}`, lang)}
      variant={isMobile ? 'subtitle3' : 'body1'}
      fontWeight={isMobile ? '600' : '400' }
      sx={{
        cursor: 'pointer',
        overflow: isMobile ? 'visible' : 'hidden',
        textOverflow: isMobile ? 'unset' : 'ellipsis',
        whiteSpace: isMobile ? 'normal' : 'nowrap',
        wordBreak: 'keep-all',
        '&:hover': { textDecoration: 'underline', fontWeight: '700' },
      }}
    >
      {v}
    </Typography>
  )

  const renderLecture = (v) => (
    <Typography 
      sx={{
        overflow: isMobile ? 'visible' : 'hidden',
        textOverflow: isMobile ? 'unset' : 'ellipsis',
        whiteSpace: isMobile ? 'normal' : 'nowrap',
        wordBreak: 'keep-all',
      }}
    >
      {v?.trim() || '-'}
    </Typography>
  )

  const renderStatus = (v) => (
    <Typography
      fontWeight={600}
      sx={{
        color: v === '대기' ? 'secondary.main' : 'text.primary',
      }}
    >
      {v}
    </Typography>
  )

  return [
    { field: 'rowNo', role: 'no', headerName: '번호', sortable: false, width: '70px', align: 'center', hideTitleInCard: true },
    { field: 'bbsCategoryName', role: 'qCategory', headerName: '분류', sortable: false, width: '120px', align: 'center' },
    { field: 'eduLectureName', role: 'lecture', headerName: '강좌명', sortable: false, width: '250px', align: 'left', render: renderLecture },
    { field: 'bulletinTitle', role: 'title', headerName: '제목', sortable: false, align: 'left', render: renderTitle },
    { field: 'replyStatus', role: 'status', headerName: '답변유무', sortable: false, width: '85px', align: 'center', render: renderStatus },
    { field: 'registDttm', role: 'date', headerName: '등록일', sortable: false, width: '120px', align: 'center' },
  ]
}

/* 상단 검색 */
export function InquiryToolbar({ isMobile, status, lecture, onStatusChange, onLectureChange, lectures = [] }) {
  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      alignItems="center"
      spacing={1}
      sx={{
        flex: 'auto',
        width: isMobile ? '100%' : 'auto'
      }}
    >
      {/* 상태 필터 */}
      <FormControl sx={{ flex: 'auto', width: isMobile ? '100%' : '220px', }}>
        <Typography
          component="label"
          sx={visuallyHidden}
          htmlFor="answer-select"
        >
          답변유무 선택
        </Typography>
        <Select
          name="status"
          value={status}
          onChange={(e) => onStatusChange?.(e.target.value)}
          displayEmpty
          MenuProps={{
            PaperProps: {
              sx: {
                width: '100%',
                maxWidth: (theme) => isMobile ? `calc(100vw - ${theme.spacing(8)})` : '220px',
              },
            },
          }}
          inputProps={{
            'id': 'answer-select',
            'labelId': 'answer-select'
          }}
          sx={{ 
            width: '100%',
            maxWidth: (theme) => isMobile ? `calc(100vw - ${theme.spacing(8)})` : '220px',
          }}
        >
          <MenuItem value="전체">전체</MenuItem>
          <MenuItem value="Y">완료</MenuItem>
          <MenuItem value="N">대기</MenuItem>
        </Select>
      </FormControl>

      {/* 강좌명 필터 */}
      <FormControl sx={{ flex: 'auto', width: isMobile ? '100%' : '320px', }}>
        <Typography
          component="label"
          sx={visuallyHidden}
          htmlFor="lecture-select"
        >
          강좌명 선택
        </Typography>
        <Select
          name="lecture"
          value={lecture}
          onChange={(e) => onLectureChange?.(e.target.value)}
          displayEmpty
          MenuProps={{
            PaperProps: {
              sx: {
                width: '100%',
                maxWidth: (theme) => isMobile ? `calc(100vw - ${theme.spacing(8)})` : '320px',
              },
            },
          }}
          inputProps={{
            'id': 'lecture-select',
            'labelId': 'lecture-select'
          }}
          sx={{ 
            width: '100%',
            maxWidth: (theme) => isMobile ? `calc(100vw - ${theme.spacing(8)})` : '320px',
          }}
        >
          <MenuItem value="전체">전체</MenuItem>
          {lectures.map((lec) => (
            <MenuItem key={lec.code} value={lec.code}>
              {lec.codeNm}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  )
}

export default function MyInquiry() {
  const navigate = useNavigate()
  const { isMobile } = useIsMobile()
  const lang = useLang() 
  const { user, loading, isAuthReady } = useAuth();

  const [lectures, setLectures] = useState([]);
  const [statusFilter, setStatusFilter] = useState('전체') 
  const [lectureFilter, setLectureFilter] = useState('전체')
  const [confirmedStatus, setConfirmedStatus] = useState('전체') 
  const [confirmedLecture, setConfirmedLecture] = useState('전체') 
  const [confirmedSearch, setConfirmedSearch] = useState('')

  useEffect(() => {
    const getLectures = async () => {
      try {
        const response = await fetchMyCourseComboList({ params: { accountId: user.accountId } });
        setLectures(response.data || []);
      } catch (error) {
        console.error("Failed to fetch lectures:", error);
      }
    };
    getLectures();
  }, []);

  const handleStatusChange = useCallback((value) => {
    setStatusFilter(value)
  }, [])

  const handleLectureChange = useCallback((value) => {
    setLectureFilter(value)
  }, [])

  const fetcher = useCallback(
    async ({ page, pageSize, sort }) => {
      const params = {
        page: page,
        size: pageSize,
        sort: sort ? `${sort.field},${sort.direction}` : 'no,desc',
        replyYn: confirmedStatus !== '전체' ? confirmedStatus : undefined,
        eduLectureId: confirmedLecture !== '전체' ? confirmedLecture : undefined,
        keyword: confirmedSearch || undefined,
        accountId: user.accountId
      };

      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      try {
        const response = await fetchMyInquiryList({ params });
        const { content, totalElements, totalPages } = response.data;
        return { items: content, total: totalElements, totalPages };
      } catch (error) {
        console.error("Failed to fetch inquiries:", error);
        return { items: [], total: 0, totalPages: 1 };
      }
    },
    [confirmedStatus, confirmedLecture, confirmedSearch]
  )

  const columns = useMemo(
    () => createInquiryTableColumns({ isMobile, lang }),
    [isMobile, lang]
  )

  return (
    <BoardList
      view="table"
      caption="나의 문의 목록(번호, 분류, 강좌명, 제목, 답변유무, 등록일로 구성)"
      columns={columns}
      fetcher={fetcher}
      TableComponent={BoardListTable}
      initialSort={{ field: 'no', direction: 'desc' }}
      initialPage={1}
      initialPageSize={10}
      toolbarLeft={
        <InquiryToolbar
          isMobile={isMobile}
          status={statusFilter}
          lecture={lectureFilter} 
          onStatusChange={handleStatusChange}
          onLectureChange={handleLectureChange}
          lectures={lectures}
        />
      }
      onSearchSubmit={(searchValue) => {
        setConfirmedStatus(statusFilter)
        setConfirmedLecture(lectureFilter)
        setConfirmedSearch(searchValue)
      }}
      emptyMessage="나의 문의 내역이 없습니다." 
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        sx={{ mt: isMobile ? 3 : 5 }}
      >
        <Button
          variant="contained"
          color="primary"
          size="large" 
          sx={{ width: isMobile ? '100%' : 'auto', mt: isMobile ? 3 : 0 }}
          onClick={() => navigate(buildLangHref('/mypage/inquiry/write', lang))}
        >
          등록
        </Button>
      </Stack>
    </BoardList>
  )
}

